import Box from '@mui/material/Box';
import classNames from 'classnames';

import Card from '../../../components/Card';
import Loading from '../../../components/Loading';
import ECharts from '../../../components/ECharts';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { formatDisplayNumber } from '../../../utils/format';
import { generateMinMaxChartData, getMinMax } from '../../../utils/helpers';
import { parseDate } from '../../../utils/date';
import { HOME_CHART_COLOR } from '../../../constants/colors';

import {
  transferExchangeHistoryData,
  getDayAgoPercent,
  getIndexNameByStock,
  getIndexTooltipForTradingChart,
  getPeriod,
  transferExchangeHistoryChartData,
} from '../Dashboard.helpers';
import * as Styles from '../Dashboard.styles';

const TradingVolume = ({
  isLoading,
  exchangeHistory,
  isDarkMode,
  selectedStock,
  handlePeriodClick,
  selectedPeriod,
  isTradingVolumeChartLoading,
  tradingChartData,
}) => {
  const { width } = useWindowDimensions();
  const data = transferExchangeHistoryData(
    exchangeHistory,
    'KLGDkhoplenh',
    'KLGDthoathuan',
    'klgd',
    selectedStock
  );

  const chartData = transferExchangeHistoryChartData(
    tradingChartData,
    'klgd',
    selectedStock
  );

  const getInterval = (xAxisData) => {
    if (width >= 1030) {
      return Math.floor(xAxisData.length / 3);
    }

    if ((width < 1030 && width >= 768) || (width < 530 && width >= 390)) {
      return Math.floor(xAxisData.length / 2);
    }

    if (width < 390) {
      return xAxisData.length;
    }

    return Math.floor(xAxisData.length / 3);
  };

  const getChartOption = (xAxisData, seriesData, series2Data) => {
    const bgColor = isDarkMode ? '#1a1c23' : '#fff';
    const color = isDarkMode ? '#e5e7eb' : '#24262d';
    const closePriceMinMax = getMinMax(series2Data);
    const minMaxAxisRight = generateMinMaxChartData(
      closePriceMinMax[0],
      closePriceMinMax[1],
      5,
      100
    );

    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            formatter: function (params) {
              if (params.axisDimension === 'y') {
                return formatDisplayNumber(params.value.toFixed(2));
              }
              if (typeof params.value === 'string') {
                return params.value;
              }
              return formatDisplayNumber(Number(params.value).toFixed(0));
            },
          },
        },
        position: function (point, params, dom, rect, size) {
          if (width > 1024) {
            return [
              point[0] - size.contentSize[0] / 100,
              point[1] - size.contentSize[1],
            ];
          }
          if (point[0] + point[0] / 2 > size.viewSize[0]) {
            return [
              point[0] - size.contentSize[0],
              point[1] - size.contentSize[1],
            ];
          }
          return [point[0] / 2, point[1] - size.contentSize[1]];
        },
        formatter: (params) => {
          const item = tradingChartData.find(
            (e) => parseDate(e.DTYYYYMMDD) === params[0].axisValue
          );
          return `
            <div class="inline-block">
              <div class="font-bold">${params[0].axisValue}</div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-1">
                <div>
                  <div>
                    <span>KLGD:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.klgd)} cp
                    </span>
                  </div>
                  <div>
                    <span>Khớp lệnh:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.KLGDkhoplenh)} cp
                    </span>
                  </div>
                  <div>
                    <span>Thỏa thuận:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.KLGDthoathuan)} cp
                    </span>
                  </div>
                </div>
                <div class="ml-2">
                  <div>
                    <span>HOSE:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.nvIndex.klgd)} cp
                    </span>
                  </div>
                  <div>
                    <span>HNX:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.hnxIndex.klgd)} cp
                    </span>
                  </div>
                  <div>
                    <span>UPCOM:</span>
                    <span class="ml-1 inline-flex font-bold">
                      ${formatDisplayNumber(item.upcomIndex.klgd)} cp
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <span>${getIndexNameByStock(selectedStock)}:</span>
                <span class="ml-1 inline-flex">
                  ${getIndexTooltipForTradingChart(item, selectedStock)}
                </span>
              </div>
            </div>
          `;
        },
      },
      color: [
        isDarkMode ? HOME_CHART_COLOR.dark : HOME_CHART_COLOR.light,
        isDarkMode ? HOME_CHART_COLOR.white : HOME_CHART_COLOR.black,
      ],
      grid: {
        left: 0,
        right: 0,
        top: 10,
        bottom: 30,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            show: false,
          },
          data: xAxisData,
          axisLabel: {
            color,
            showMinLabel: true,
            interval: getInterval(xAxisData),
            align: 'left',
          },
          name: xAxisData[xAxisData.length - 1],
          nameLocation: 'end',
          nameGap: 0,
          nameTextStyle: {
            color,
            align: 'right',
            verticalAlign: 'top',
            padding: [8, 0, 0, 0],
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        {
          type: 'value',
          axisLabel: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          showSymbol: false,
          min: minMaxAxisRight.min,
          max: minMaxAxisRight.max,
          interval: (minMaxAxisRight.max - minMaxAxisRight.min) / 5,
        },
      ],
      series: [
        {
          name: 'KLGD',
          type: 'bar',
          emphasis: {
            focus: 'series',
          },
          data: seriesData,
          barMaxWidth: 40,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)',
          },
        },
        {
          data: series2Data,
          type: 'line',
          name: 'Giá đóng cửa',
          yAxisIndex: 1,
          showSymbol: false,
        },
      ],
    };
  };

  return (
    <Card
      title="Khối Lượng Giao Dịch (KLGD)"
      linkUrl="/thong-ke-khoi-luong-giao-dich"
      linkText="Xem chi tiết"
      linkClassName="whitespace-nowrap ml-2 inline-flex"
    >
      {isLoading ? <Styles.EmptyTag /> : null}
      <Loading isLoading={isLoading} className="white" />
      {!isLoading ? (
        <Box>
          {!data ? (
            <Box>Không có dữ liệu</Box>
          ) : (
            <Box className="text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-1">
                <div>
                  <Box className="flex items-center">
                    <span>KLGD:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.totalCurrent)} cp
                    </span>
                    {getDayAgoPercent(data.totalCurrent, data.totalDayAgo)}
                  </Box>
                  <Box className="mt-1">
                    <span>Khớp lệnh:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.totalAuction)} cp
                    </span>
                  </Box>
                  <Box className="mt-1">
                    <span>Thỏa thuận:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.totalPutThrough)} cp
                    </span>
                  </Box>
                </div>
                <div>
                  <Box>
                    <span>HOSE:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.vnIndex)} cp
                    </span>
                  </Box>
                  <Box className="mt-1">
                    <span>HNX:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.hnxIndex)} cp
                    </span>
                  </Box>
                  <Box className="mt-1">
                    <span>UPCOM:</span>
                    <span className="ml-1 inline-flex">
                      {formatDisplayNumber(data.upcomIndex)} cp
                    </span>
                  </Box>
                </div>
              </div>
            </Box>
          )}
        </Box>
      ) : null}
      <Box>
        {getPeriod('volume', handlePeriodClick, selectedPeriod)}
        <div
          className={classNames(
            'relative w-full',
            isTradingVolumeChartLoading ? 'min-h-[65px]' : ''
          )}
        >
          <Loading isLoading={isTradingVolumeChartLoading} className="white" />
          {!isTradingVolumeChartLoading ? (
            <>
              {!tradingChartData?.length ? (
                <Box>Không có dữ liệu</Box>
              ) : (
                <ECharts
                  options={getChartOption(
                    chartData.xAxisData,
                    chartData.seriesData,
                    chartData.series2Data
                  )}
                  height="250px"
                />
              )}
            </>
          ) : null}
        </div>
      </Box>
    </Card>
  );
};

export default TradingVolume;
